<template>
  <b-modal v-model="showModal" ref="hoursModal" :modal-class="'hours-modal'" persistent="true" :title="modalTitle" @hide="closeModal">
    <b-row>
      <div class="timepicker-wrapper p-4 mb-4">
        <div class="timepicker-main">
          <div>
            <div class="hours-modal-label">{{ $gettext('Duration') }}</div>
            <TimePicker v-model="event.duration" :key="durationKey" :picker-size="'xl'" :ref="'timepicker_duration'" :data-type="'duration'" @input="changeDuration" @focus-duration="setTimepickerFocus"/>
          </div>
        </div>
        <div class="timepicker-startend">
          <div>
            <div class="hours-modal-label">{{ $gettext('Start time') }}</div>
            <TimePicker v-model="event.start" :data-type="'date'" :ref="'timepicker_start'" @input="startTimeChanged" @focus-duration="setTimepickerFocus"/>
          </div>
          <div>
            <div class="hours-modal-label">{{ $gettext('End time') }}</div>
            <TimePicker v-model="event.end" :data-type="'date'" :ref="'timepicker_end'" @input="endTimeChanged" @focus-duration="setTimepickerFocus"/>
          </div>
        </div>
        <div class="form-required text-danger text-center" v-if="formWarnings.duration">
          <span>{{ $gettext('Start time must be before end time') }}</span>
        </div>
      </div>
    </b-row>
    <b-form>

      <b-form-group v-if="!isRecurrent || (isRecurrent && value === 'edit')">
        <div class="hours-modal-label">{{ $gettext('Date') }}</div>
        <b-form-datepicker v-model="datepickerDate" @input="updateSelectedDate" :disabled="isRecurrent && value === 'edit'" />
        <div class="form-required text-danger" v-if="formWarnings.datepicker">
          <span>{{ $gettext('Select a date to save this entry') }}</span>
        </div>
      </b-form-group>

      <b-form-group v-if="isRecurrent && value !== 'edit'">
        <div class="hours-modal-label">{{ $gettext('Start date') }}</div>
        <b-datepicker v-model="recurrentHour.start_date" :disabled="value === 'edit'"></b-datepicker>
      </b-form-group>
    
      <b-form-group>
        <div class="hours-modal-label">{{ $gettext('Description') }}</div>
        <b-form-textarea v-model="event.description" :placeholder="$gettext('Activity description...')" rows="3" no-resize />
      </b-form-group>

      <b-form-group>
        <div class="hours-modal-label">{{ $gettext('Project') }}</div>
        <HoursProjectSelector id="project-selector" v-model="event.project" :is-invoiced="event.invoiced" @change="projectSelected" @deselect="projectDeselected" />
        <b-tooltip :disabled="!event.invoiced" target="project-selector">{{ $gettext('You cannot modify project if this hour is invoiced.') }}</b-tooltip>
        
        <div class="form-required text-danger" v-if="formWarnings.project">
          <span>{{ $gettext('Select a project to save this entry') }}</span>
        </div>
      </b-form-group>

      <b-form-row class="fs-base">
        <b-col cols="12" lg="6">
          <b-form-group >
            <div class="hours-modal-label">{{ $gettext('Status') }}</div>
            <div id="tooltip-status">
              <b-form-checkbox v-model="event.invoiced" switch :disabled="isUnassigned" class="hours-modal-status" >
                {{ (event.invoiced) ? $gettext('Invoiced') : $gettext('Uninvoiced') }}
              </b-form-checkbox>
            </div>
            <b-tooltip :disabled="!isUnassigned" target="tooltip-status">{{ $gettext('Assign a project to update this hour status.') }}</b-tooltip>
          </b-form-group>
        </b-col>
        <b-col cols="12" lg="6">
          <b-form-group>
            <div class="hours-modal-label">{{ $gettext('Created by') }}</div>
            <HoursUserSelector v-model="event.created_by" @change="userSelected" />
            <div class="form-required text-danger" v-if="formWarnings.user">
              <span>{{ $gettext('Select a user to save this entry') }}</span>
            </div>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row class="fs-base" v-if="value !== 'edit'">
        <b-col cols="12" lg="6">
          <b-form-group>
            <b-form-checkbox v-model="isRecurrent" @input="onIsRecurrentChange" switch class="hours-modal-status" :key="recurrentKey">
              {{ (isRecurrent) ? $gettext('Recurring') : $gettext('Non-recurring') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-form-row>

      <div v-if="isRecurrent">
        <b-form-group v-if="value === 'edit'">
          <div class="hours-modal-label">{{ $gettext('Start date') }}</div>
          <span>{{ recurrentHour.start_date | formatDate }}</span>
        </b-form-group>

        <b-form-group>
          <div class="hours-modal-label">{{ $gettext('End date') }}</div>
          <span v-if="value === 'edit'">{{ recurrentHour.end_date | formatDate }}</span>
          <b-datepicker v-if="value !== 'edit'" v-model="recurrentHour.end_date" @input="clearEndDateWarnings()" required></b-datepicker>
          <div class="form-required text-danger" v-if="formWarnings.end_date">
            <span>{{ $gettext('Select an end date for the recurrency') }}</span>
          </div>
          <div class="form-required text-danger" v-if="formWarnings.end_date_invalid">
            <span>{{ $gettext('The end date must be greater than the start date') }}</span>
          </div>
          <div class="form-required text-danger" v-if="formWarnings.end_date_big">
            <span>{{ $gettext('You cannot create recurrent hours for a period longer than 1 year') }}</span>
          </div>
        </b-form-group>

        <b-form-group>
          <div class="hours-modal-label">{{ $gettext('Frequency') }}</div>
          <span v-if="value === 'edit'">{{ frequencyText }}</span>
          <b-form-select v-if="value !== 'edit'" v-model="recurrentHour.frequency" :options="frequencyOptions" @input="clearFrequencyWarning()" required/>
          <div class="form-required text-danger" v-if="formWarnings.frequency">
            <span>{{ $gettext('Select the frequency of the recurrent hour') }}</span>
          </div>
          <div class="form-required text-danger" v-if="formWarnings.frequency_monthly_invalid_dates">
            <span>{{ $gettext('You cannot plan monthly recurring hours on the 29th, 30th or 31st') }}</span>
          </div>
        </b-form-group>
      </div>
    </b-form>
    <template #modal-footer="{ ok, close }">
      <GekkoButton 
        v-if="value !== 'create'"
        @click="deleteEvent()"
        :text="$gettext('Delete')"
        class="text-danger mr-auto px-0"
        variant="link"
        size="sm"
      />
      <GekkoButton 
        @click="close()"
        :text="$gettext('Cancel')"
        class="text-primary float-right"
        variant="link"
        size="sm"
      />
      <GekkoButton 
        @click="ok()"
        :text="$gettext('Save')"
        class="float-right position-relative"
        size="sm"
        :loading="isWaitingForResponse"
      />
    </template>
  </b-modal>
</template>
<script>
import TimePicker from "@/components/hours/TimePicker";
import HoursProjectSelector from "@/components/hours/HoursProjectSelector";
import HoursUserSelector from "@/components/hours/HoursUserSelector";
import GekkoButton from "@/components/GekkoButton";
import HourService from "@/store/services/hour.service.js";
import { mapGetters, mapMutations } from 'vuex';

export default {
  name: "HourEditModal",
  components: { HoursProjectSelector, HoursUserSelector, TimePicker, GekkoButton },
  props: {
    value: {
      type: String,
      default: null,
    },
    selectedEvent: {
      type: Object,
      default: null,
    }
  },
  watch: {
    selectedEvent() {
      this.setUpModal();
    }
  },
  data() {
    return {
      event: {},
      durationKey: 0,
      formWarnings: {
        project: false,
        duration: false,
        datepicker: false,
        user: false,
        end_date: false,
        end_date_invalid: false,
        end_date_big: false,
        frequency: false,
        frequency_monthly_invalid_dates: false
      },
      datepickerDate: null,
      durationTimepickerFocus: false,
      isRecurrent: false,
      recurrentHour: {
        frequency: null,
        start_date: null,
        end_date: null
      },
      frequencyOptions: [
        {value: null, text: this.$gettext("-- Select a frequency --")},
        {value: "every_day", text: this.$gettext("Every day")},
        {value: "every_working_day", text: this.$gettext("Every working day")},
        {value: "every_week", text: this.$gettext("Every week")},
        {value: "every_month", text: this.$gettext("Every month")}
      ],
      recurrentKey: 0
    }
  },
  computed: {
    ...mapGetters({
      isWaitingForResponse: 'main/isWaitingForResponse'
    }),
    showModal: {
      get: function() {
        return this.value !== null;
      },
      set: function () {
        this.$emit("input", null);
      }
    },
    modalTitle () {
      if (this.value === 'create') {
        return this.$gettext("New hour entry");
      } else {
        return this.$gettext("Edit hour");
      }
    },
    isUnassigned () {
      return (!this.event.project || !this.event.project.id)
    },
    frequencyText() {
      let frequencyOption = this.frequencyOptions.find((option) => option.value === this.recurrentHour.frequency);
      return frequencyOption.text;
    }
  },
  created () {
    this.setUpModal();
  },
  methods: {
    ...mapMutations({
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE'
    }),
    setUpModal() {
      this.event = { ...this.selectedEvent }
      
      if (typeof this.event.recurrent_time_entry === "number")
        this.isRecurrent = true;
      else if (this.event.recurrent_time_entry && this.event.recurrent_time_entry.length)
        this.isRecurrent = true;

      if (this.isRecurrent) {
        HourService.retrieveRecurrentHour(this.event.recurrent_time_entry).then((response) => {
          this.recurrentHour = response.data;

          if (response.data.interval === "weekly")
            this.recurrentHour.frequency = "every_week";
          else if (response.data.interval === "monthly")
            this.recurrentHour.frequency = "every_month";
          else if (response.data.interval === "daily") {
            let daysArr = response.data.days.split(',');

            if (daysArr.length === 5)
              this.recurrentHour.frequency = "every_working_day";
            else if (daysArr.length === 7)
              this.recurrentHour.frequency = "every_day";
          }
        });
      }
      
      if (this.event.start) {
        let freqWeekOpt = this.frequencyOptions.find(freq => freq.value === "every_week");
        switch (this.event.start.getDay()) {
          case 0:
            freqWeekOpt.text = this.$gettext("Every week on Sundays");
            break;
          case 1:
            freqWeekOpt.text = this.$gettext("Every week on Mondays");
            break;
          case 2:
            freqWeekOpt.text = this.$gettext("Every week on Tuesdays");
            break;
          case 3:
            freqWeekOpt.text = this.$gettext("Every week on Wednesdays");
            break;
          case 4:
            freqWeekOpt.text = this.$gettext("Every week on Thursdays");
            break;
          case 5:
            freqWeekOpt.text = this.$gettext("Every week on Fridays");
            break;
          case 6:
            freqWeekOpt.text = this.$gettext("Every week on Saturdays");
            break;
          default:
            break;
        }
        let freqMonthOpt = this.frequencyOptions.find(freq => freq.value === "every_month");
        switch (this.event.start.getDate()) {
          case 1:
            freqMonthOpt.text = `${this.$gettext("Every month on the 1st")}`;
            break;
          case 2:
            freqMonthOpt.text = `${this.$gettext("Every month on the 2nd")}`;
            break;
          case 3:
            freqMonthOpt.text = `${this.$gettext("Every month on the 3rd")}`;
            break;
          case 21:
            freqMonthOpt.text = `${this.$gettext("Every month on the 21st")}`;
            break;
          case 22:
            freqMonthOpt.text = `${this.$gettext("Every month on the 22nd")}`;
            break;
          case 23:
            freqMonthOpt.text = `${this.$gettext("Every month on the 23rd")}`;
            break;
          default:
            freqMonthOpt.text = `${this.$gettext("Every month on the")} ${this.event.start.getDate()}th`;
            break;
        }
      }

      this.datepickerDate = this.event.start
      this.setDuration()
    },
    setTimepickerFocus (value) {
      this.durationTimepickerFocus = value
    },
    updateSelectedDate (inputDate) {
      this.formWarnings.datepicker = false
      this.event.start = this.setHoursNewDate(this.event.start, inputDate)
      // update end date according to duration
      this.updateEndTime()
    },
    setHoursNewDate (originalDate, inputDate) {
      const newDate = new Date(inputDate)
      const startDate = new Date(originalDate)
      const startHours = startDate.getHours()
      const startMinutes = startDate.getMinutes()
      newDate.setHours(startHours);
      newDate.setMinutes(startMinutes);
      return newDate
    },
    deleteEvent () {
      if (this.isRecurrent)
        this.$emit("delete-recurrent-event", this.event);
      else
        this.$emit("delete-event", this.event);
      this.hideModal();
    },
    hideModal () {
      this.$refs.hoursModal.hide()
    },
    onSubmit (){
      this.setWaitingForResponse(true);
      if (this.isRecurrent) {
        let hour = HourService.eventToTimeentry(this.event);
        let data = HourService.prepareRecurrentHour(this.recurrentHour, hour);

        data.action = this.value;
        
        hour.id = this.event.id;
        data['selected_hour'] = hour;
        data.created_by = data.template_time_entry.created_by
        this.$emit("save-recurrent-hour", data);
      } else {
        this.$emit("save-hour", { action: this.value, event: this.event });
      }
    },
    closeModal (event) {
      if (event.trigger === 'ok') {
        if(this.validateModal()) {
          this.onSubmit();
          this.resetRecurrentData();
        } else {
          this.$store.commit('setAlert', { title: this.$gettext('Missing fields'), text: this.$gettext('Please check form warnings.'), variant: 'danger', duration: 4 })
          event.preventDefault();
        }
      } else {
        this.$emit("cancel", this.value);
        this.resetRecurrentData();
      }
    },
    projectSelected (value) {
      this.formWarnings.project = false
      this.event.project = value;
    },
    projectDeselected () {
      this.formWarnings.project = false
      this.event.project = null;
    },
    userSelected (value) {
      this.formWarnings.user = false
      this.event.created_by = value;
    },
    changeDuration (duration) {
      const durationInMs  = ((parseInt(duration.HH) * 60) + parseInt(duration.mm)) * 60000
      const endTime = new Date(this.event.start.getTime() + durationInMs);
      this.event.end = endTime
    },
    startTimeChanged () {
      if (!this.durationTimepickerFocus) {
        this.updateEndTime()
      }
    },
    endTimeChanged () {
      if (!this.durationTimepickerFocus) {
        let testEndDate
        if(this.event.end < this.event.start) {
          // end is before start - add +1 to end date
          this.event.end.setDate(this.event.end.getDate() + 1);
        } else {
          testEndDate = new Date(this.event.end)
          testEndDate.setDate(testEndDate.getDate() - 1)
          if(testEndDate.getTime() === this.event.start.getTime()) {
            // end is just 24h after start, equalize dates
            this.event.end = testEndDate
          } else {
            testEndDate = new Date(this.event.start)
            testEndDate.setDate(testEndDate.getDate() +1)
            if (this.event.end > testEndDate) {
              // end is more than 24h after start, substract 1 day to end
              this.event.end.setDate(this.event.end.getDate() - 1);
            }
          }

        }
        this.setDuration();
      }
    },
    updateEndTime () {
      // get current duration time in miliseconds to add it to start => and get end time
      const duration  = ((parseInt(this.event.duration.HH) * 60) + parseInt(this.event.duration.mm)) * 60000
      const endTime = new Date(this.event.start.getTime() + duration);
      this.event.end = endTime
    },
    checkEndTime (endTime) {
      return (this.event.start.getFullYear() === endTime.getFullYear() &&
              this.event.start.getMonth() === endTime.getMonth() &&
              this.event.start.getDate() === endTime.getDate())
    },
    setDuration () {
      if(!this.event.start || !this.event.end) {
        this.event.start = new Date()
        this.event.end = new Date().addHours(1)
        this.event.duration = { HH: 1, mm: 0 } 
      } else {
        const diffMinutes = Math.ceil((this.event.end - this.event.start) / 1000 / 60)
        const diffHours = Math.floor(diffMinutes / 60)
        this.event.duration = { HH: diffHours, mm: diffMinutes - (diffHours * 60) } 
      }
      this.durationKey++
    },
    validateDuration () {
      if (this.event.end < this.event.start) {
        this.formWarnings.duration = true
        this.$store.commit('setAlert', { title: this.$gettext('Bad input'), text: this.$gettext('Start time must be before end time.'), variant: 'danger', duration: 4 })
      } else {
        this.formWarnings.duration = false
      }
    },
    validateModal () {
      if(!this.datepickerDate) {
        this.formWarnings.datepicker = true
      } else {
        this.formWarnings.datepicker = false
      }

      this.formWarnings.project = false

      if(this.event.created_by && this.event.created_by.id) {
        this.formWarnings.user = false
      } else {
        this.formWarnings.user = true
      }

      if (this.isRecurrent) {
        let auxStartDate;
        let auxEndDate;

        if (typeof this.recurrentHour.start_date === "string") {
          auxStartDate = new Date(this.recurrentHour.start_date);
        } else {
          auxStartDate = this.recurrentHour.start_date;
        }
        
        if (typeof this.recurrentHour.end_date === "string") {
          auxEndDate = new Date(this.recurrentHour.end_date);
        } else {
          auxEndDate = this.recurrentHour.end_date;
        }

        if (!this.recurrentHour.end_date) {
          this.formWarnings.end_date = true;
        } else {
          this.formWarnings.end_date = false;

          if (auxStartDate > auxEndDate)
            this.formWarnings.end_date_invalid = true;
          else
            this.formWarnings.end_date_invalid = false;
          
          const diffTime = Math.abs(auxEndDate - auxStartDate);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays > 365) // 1 year
            this.formWarnings.end_date_big = true;
          else
            this.formWarnings.end_date_big = false;
        }

        if (!this.recurrentHour.frequency)
          this.formWarnings.frequency = true;
        else {
          this.formWarnings.frequency = false;

          if (this.recurrentHour.frequency === "every_month" && auxStartDate.getDate() > 28) // 29th, 30th, 31st excluded
            this.formWarnings.frequency_monthly_invalid_dates = true;
          else
            this.formWarnings.frequency_monthly_invalid_dates = false;
        }
      }

      return Object.values(this.formWarnings).every(item => item === false)
    },
    resetRecurrentData() {
      this.isRecurrent = false;
      this.recurrentHour.start_date = null;
      this.recurrentHour.frequency = null;
      this.recurrentHour.end_date = null;
    },
    onIsRecurrentChange(isRecurrent) {
      if (this.$store.getters['getIsPremium']) {
        if (isRecurrent)
          this.recurrentHour.start_date = this.datepickerDate;
        else
          this.datepickerDate = this.recurrentHour.start_date;
      } else {
        this.isRecurrent = false;
        this.recurrentKey++;
        this.$store.commit('togglePremiumModal', 'web_hours_recurrententry');
      }
    },
    clearEndDateWarnings() {
      this.formWarnings.end_date = false;
      this.formWarnings.end_date_invalid = false;
      this.formWarnings.end_date_big = false;
    },
    clearFrequencyWarning() {
      this.formWarnings.frequency = false;
      this.formWarnings.frequency_monthly_invalid_dates = false;
    }
  }
}
</script>
<style lang="scss">
.hours-modal {
  .modal-dialog { max-width: 365px; }
  .modal-content {
    overflow: visible;
    border-radius: $radiusLg !important;
    .modal-header, .modal-footer { padding: 0.75rem; }
    .modal-header { border: 0; }
    .modal-footer { > * { margin: 0; } }
    .modal-body {
      padding-top: 0;
      .timepicker-wrapper {
        width: 100%;
        background: $inputBackgroundColor;
        box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
        .timepicker-main {
          display: flex; justify-content: center; margin-bottom: 1rem;
        }
        .timepicker-startend {
          display: flex;
          justify-content: center;
          > div {
            &:first-child { margin-right: 5px; }
            &:last-child { margin-left: 5px; }
          }
        }
      }
      .hours-modal-label {
        font-size: 12px;
        color: grey;
        display: block;
      }
      textarea {
        border: 0;
        box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
        background-color: $inputBackgroundColor;
      }
      .form-required {
        font-size: 14px;
        line-height: 1;
        margin-top: 5px;
      }
    }
  }
  .fs-base { font-size: 1rem; }
  .hours-modal-status {
    height: 45px;
    display: flex;
    align-items: center;
  }
}

</style>